<script setup>
import { ref, computed } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import { useQueryClient } from '@tanstack/vue-query';
import { useToast } from 'vue-toastification';
import { createMapOverlay } from '@gpx-spa/queries';
import { setTitle } from '@/utils/title';
import { COMMON_SAVE_SUCCESS, COMMON_SAVE_ERROR } from '@/config/constants';
import ThreeColumnLayout from '@/components/_common/ThreeColumnLayout.vue';
import MapControlPanel from '@/components/_common/navbar/MapControlPanel.vue';
import CommonGoBack from '@/components/_common/CommonGoBack.vue';
import { topBarInsetClass } from '@/components/_common/navbar/topBarInset';
import OverlayEditor from '@/components/overlay/OverlayEditor.vue';
import OverlayEditorMap from '@/components/overlay/OverlayEditorMap.vue';
import { normalizeCorners } from '@/utils/map/overlayCorners';

import { useSynchronizedSidebars } from '@/composables/useSynchronizedSidebars';
useSynchronizedSidebars();

import { useResetChatOnSidebarClose } from '@/composables/useResetChatOnSidebarClose';
useResetChatOnSidebarClose();

import { useResetChatOnAccountOrUserChange } from '@/composables/useResetChatOnAccountOrUserChange';
useResetChatOnAccountOrUserChange();

const store = useStore();
const router = useRouter();
const toast = useToast();
const queryClient = useQueryClient();

const accountId = computed(() => store.getters['auth/accountId']);
const { mutateAsync: createMutation } = createMapOverlay();

const imageUrl = ref(null);
const corners = ref(null);
const opacity = ref(1);
const isSaving = ref(false);

function computeBottomRight(c) {
  return {
    lat: c.bottomLeft.lat + (c.topRight.lat - c.topLeft.lat),
    lng: c.bottomLeft.lng + (c.topRight.lng - c.topLeft.lng),
  };
}

async function handleSave({ name, opacity: opacityVal, file, corners: c }) {
  isSaving.value = true;
  try {
    const normalizedCorners = normalizeCorners(c);
    if (!normalizedCorners) {
      toast.error('Invalid overlay corners. Please adjust corners and try again.');
      return;
    }

    const bottomRight = computeBottomRight(normalizedCorners);
    if (!Number.isFinite(bottomRight.lat) || !Number.isFinite(bottomRight.lng)) {
      toast.error('Invalid overlay corners. Please adjust corners and try again.');
      return;
    }

    const formData = new FormData();
    formData.append('name', name);
    formData.append('opacity', opacityVal);
    formData.append('file', file);
    formData.append('corners[topLeft][lat]', normalizedCorners.topLeft.lat);
    formData.append('corners[topLeft][lng]', normalizedCorners.topLeft.lng);
    formData.append('corners[topRight][lat]', normalizedCorners.topRight.lat);
    formData.append('corners[topRight][lng]', normalizedCorners.topRight.lng);
    formData.append('corners[bottomLeft][lat]', normalizedCorners.bottomLeft.lat);
    formData.append('corners[bottomLeft][lng]', normalizedCorners.bottomLeft.lng);
    formData.append('corners[bottomRight][lat]', bottomRight.lat);
    formData.append('corners[bottomRight][lng]', bottomRight.lng);

    await createMutation({ accountId: accountId.value, formData });
    await queryClient.invalidateQueries({ queryKey: ['account-map-overlays'] });
    toast.success(COMMON_SAVE_SUCCESS);
    router.push({ name: 'OverlayListView' });
  } catch (e) {
    console.error('Overlay create error:', e);
    toast.error(COMMON_SAVE_ERROR);
  } finally {
    isSaving.value = false;
  }
}

function handleCancel() {
  router.push({ name: 'OverlayListView' });
}

setTitle('Create Map Overlay');
</script>

<template>
  <ThreeColumnLayout>
    <template #leftTop>
      <div :class="topBarInsetClass">
        <CommonGoBack label="All Overlays" @click="handleCancel" />
      </div>
    </template>
    <template #left>
      <OverlayEditor
        :corners="corners"
        :isSaving="isSaving"
        @save="handleSave"
        @cancel="handleCancel"
        @update:imageUrl="imageUrl = $event"
        @update:opacity="opacity = $event"
      />
    </template>
    <template #rightTop>
      <MapControlPanel />
    </template>
    <template #right>
      <OverlayEditorMap
        :imageUrl="imageUrl"
        :opacity="opacity"
        @update:corners="corners = $event"
      />
    </template>
  </ThreeColumnLayout>
</template>
